import React, { FC, memo } from 'react';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import DateRangeInput from '@zvuk-b2b/react-uikit/ui/DateInput/DateRangeInput';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { withProviders } from 'application/helpers';
import PlayerProvider from 'application/providers/PlayerProvider';
import AddExternalRecipientsButton from '../ReportRequestForm/AddExternalRecipientsButton';
import AddressReportPointsSelect from '../ReportRequestForm/AddressReportPointsSelect';
import AudioReportActionButtons from '../ReportRequestForm/AudioReportActionButtons';
import ExternalReportRecipientsInput from '../ReportRequestForm/ExternalReportRecipientsInput';
import ReportRecipientsSelect from '../ReportRequestForm/ReportRecipientsSelect';

import DailyPromoSelect from './DailyPromoSelect';
import { DATA_TEST_IDS, DATE_RANGE_DESKTOP_MONTHS_SHOWN, DATE_RANGE_MOBILE_MONTHS_SHOWN } from './consts';
import { TEXTS } from './texts';
import type { RequestAdCampaignsByDaysReportModalProps } from './types';
import { useRequestAdCampaignsByDaysReportViewModel } from './useRequestAdCampaignsByDaysReportViewModel';

import styles from './index.module.scss';

type DailyReportModalProps = RequestAdCampaignsByDaysReportModalProps & { close: () => void };

const RequestAdCampaignsByDaysReportModal: FC<DailyReportModalProps> = ({ close, refetchReports }) => {
  const { isMobile } = useMedia();
  const {
    calendarMaxDate,
    endDate,
    externalRecipientEmails,
    isExternalRecipientsInputVisible,
    isReportAddressesLoading,
    isRequestDisabled,
    loading,
    maxDate,
    minDate,
    recipients,
    reportAddresses,
    reportAddressTags,
    selectedAddressIds,
    selectedMediafileId,
    selectedRecipientIds,
    startDate,
    onDateRangeChange,
    onExternalRecipientEmailsChange,
    onModalClose,
    onRequestSubmit,
    onSelectedAddressIdsChange,
    onSelectedMediafileIdChange,
    onSelectedRecipientIdsChange,
    onShowExternalRecipientsInput,
  } = useRequestAdCampaignsByDaysReportViewModel({ close, refetchReports });

  return (
    <ModalScreen
      title={TEXTS.MODAL_TITLE}
      elementId={DATA_TEST_IDS.MODAL}
      containerClassName={styles.container}
      contentClassName={styles.content}
      titleClassName={styles.title}
      containerActionButtonsClassName={styles.actionButtons}
      onClose={onModalClose}
      actionButtons={
        <AudioReportActionButtons
          cancelButtonElementId={DATA_TEST_IDS.CANCEL_BUTTON}
          cancelButtonClassName={styles.cancelButton}
          closeButtonText={TEXTS.CANCEL}
          isRequestDisabled={isRequestDisabled}
          isRequestLoading={loading}
          requestButtonText={TEXTS.REQUEST}
          submitButtonElementId={DATA_TEST_IDS.SUBMIT_BUTTON}
          submitButtonClassName={styles.submitButton}
          onClose={onModalClose}
          onRequestSubmit={onRequestSubmit}
        />
      }>
      <div className={styles.description}>{TEXTS.MODAL_DESCRIPTION}</div>
      <div className={styles.field}>
        <label
          className={styles.label}
          htmlFor={DATA_TEST_IDS.DATE_RANGE_INPUT}>
          {TEXTS.PERIOD_LABEL}
        </label>
        <DateRangeInput
          elementId={DATA_TEST_IDS.DATE_RANGE_INPUT}
          startDate={startDate}
          endDate={endDate}
          minDate={minDate}
          maxDate={calendarMaxDate}
          label={TEXTS.PERIOD_LABEL}
          containerClassName={styles.dateRangeInput}
          inputClassName={styles.dateRangeTextInput}
          calendarButtonClassName={styles.dateRangeCalendarButton}
          monthsShown={isMobile ? DATE_RANGE_MOBILE_MONTHS_SHOWN : DATE_RANGE_DESKTOP_MONTHS_SHOWN}
          openToDate={isMobile ? minDate : startDate || maxDate}
          popperPlacement="bottom-start"
          isClearable
          withPortal={isMobile}
          onChange={onDateRangeChange}
        />
      </div>
      <DailyPromoSelect
        mediafileId={selectedMediafileId}
        onChange={onSelectedMediafileIdChange}
      />
      <AddressReportPointsSelect
        addresses={reportAddresses}
        isLoading={isReportAddressesLoading}
        tags={reportAddressTags}
        selectedAddressIds={selectedAddressIds}
        onChange={onSelectedAddressIdsChange}
      />
      <div className={styles.recipients}>
        <ReportRecipientsSelect
          afterTrigger={
            !isExternalRecipientsInputVisible ? (
              <AddExternalRecipientsButton onClick={onShowExternalRecipientsInput} />
            ) : undefined
          }
          recipients={recipients}
          selectedRecipientIds={selectedRecipientIds}
          onChange={onSelectedRecipientIdsChange}
        />
        <ExternalReportRecipientsInput
          isVisible={isExternalRecipientsInputVisible}
          emails={externalRecipientEmails}
          onChange={onExternalRecipientEmailsChange}
        />
      </div>
    </ModalScreen>
  );
};

export default memo(withProviders<DailyReportModalProps>(RequestAdCampaignsByDaysReportModal, [PlayerProvider]));
